import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { GoogleGeolocationResponseModel } from '../viewModels/GoogleGeolocationResponseModel';
import { MapsLoaderService } from './mapsLoader.service';
import { What3WordResponseModel } from '../../incidents/models/what3WordResponseModel';

@Injectable({
  providedIn: 'root',
})
export class GeoLocationService {
  public static readonly W3W_API_KEY = 'WM0ZXGVJ';
  public static readonly W3W_API_URL = 'https://api.what3words.com/v3/';

  private static readonly W3W_CONVERT_TO_COORDINATES_URL = GeoLocationService.buildW3WApiUrl('convert-to-coordinates');
  private static readonly W3W_CONVERT_TO_ADDRESS_URL =  GeoLocationService.buildW3WApiUrl('convert-to-3wa');
  private static readonly W3W_GRID_SECTION_URL = GeoLocationService.buildW3WApiUrl('grid-section');
  private static readonly W3W_AUTOSUGGEST_URL = GeoLocationService.buildW3WApiUrl('autosuggest');

  geocoder: google.maps.Geocoder;

  constructor(
    private httpClient: HttpClient,
    private readonly mapsLoaderService: MapsLoaderService
  ) {
    try {
      this.geocoder = new google.maps.Geocoder();
    } catch (error) {
      this.mapsLoaderService.loadGoogleMapsAPIs().subscribe(() => {
        this.geocoder = new google.maps.Geocoder();
      });
    }
  }

  private static buildW3WApiUrl(apiType: string): string {
    return `${GeoLocationService.W3W_API_URL}${apiType}?key=${GeoLocationService.W3W_API_KEY}`;
  }

  /**
   * Constructs a URL to retrieve the What3Words grid section for a specified bounding box.
   *
   * @param lat - The latitude of the first corner of the bounding box.
   * @param lng - The longitude of the first corner of the bounding box.
   * @param lat2 - The latitude of the opposite corner of the bounding box.
   * @param lng2 - The longitude of the opposite corner of the bounding box.
   * @returns The constructed URL as a string.
   */
  public static getWhat3WordsGridSectionURL(lat: number, lng: number, lat2: number, lng2: number): string {
    return `${GeoLocationService.W3W_GRID_SECTION_URL}&bounding-box=${lat},${lng},${lat2},${lng2}&format=geojson`;
  }

  public getPosition(): Observable<any> {
    return new Observable((observer) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => observer.next(pos),
        (_err) => undefined,
        { enableHighAccuracy: true }
      );
    });
  }

  public getPositionFromGoogleGeolocationAPI(): Observable<GoogleGeolocationResponseModel> {
    const googleKey = this.mapsLoaderService.mapConfiguration.key;
    return this.httpClient.post<GoogleGeolocationResponseModel>(
      'https://www.googleapis.com/geolocation/v1/geolocate?key=' + googleKey,
      {}
    );
  }

  public getAddressFromCoordinates(lat: number, lng: number): Observable<google.maps.GeocoderResult[]> {
    if(!this.geocoder) this.geocoder = new google.maps.Geocoder();

    const latlng = new google.maps.LatLng(lat, lng);
    return new Observable((observer) => {
      this.geocoder.geocode({
            location: latlng,
          },
          (results: google.maps.GeocoderResult[], _status: google.maps.GeocoderStatus) => {
            observer.next(results);
          }
      )
      .catch(() => undefined);
    });
  }

  public getWhat3WordsFromAddress(what3words: string): Observable<any> {
    const url = GeoLocationService.W3W_CONVERT_TO_COORDINATES_URL + '&words=' + what3words;
    return this.httpClient.get(url);
  }

  public getWhat3WordsFromCoordinates(lat: number, lng: number): Observable<What3WordResponseModel | null> {
    const url = `${GeoLocationService.W3W_CONVERT_TO_ADDRESS_URL}&coordinates=${lat},${lng}`;
    return this.httpClient.get<What3WordResponseModel>(url).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse);
      })
    );
  }

  public getWhat3WordsGridSection(lat: number, lng: number, lat2: number, lng2: number): Observable<any> {
    const url = GeoLocationService.getWhat3WordsGridSectionURL(lat, lng, lat2, lng2);
    return this.httpClient.get(url, {});
  }

  public getWhat3WordsAutoSuggest(input: string): Observable<any> {
    const url = GeoLocationService.W3W_AUTOSUGGEST_URL + '&input=' + input;
    return this.httpClient.get(url, {});
  }
}
